import AnnotationX from "@uui/AnnotationX";
import { isEmpty } from "lodash/fp";

import Skeleton from "@mui/material/Skeleton";

import {
  CoachingModule,
  CoachingModuleContent,
  CoachingModuleEmptySummary,
  CoachingModuleHeader,
  CoachingModuleSummary,
  CoachingModuleTitle,
} from "@app/analytics/components/CoachingModule";
import ErrorState from "@app/analytics/components/ErrorState";
import { NonInclusiveTermsGradientRange } from "@app/analytics/components/GradientRange/CoachingGradientRange";
import {
  SelectableChipsContainer,
  SelectableChip,
} from "@app/analytics/components/reports/styles";
import BenchmarkOverlay from "@app/analytics/MeetingReport/components/BenchmarkOverlay";
import Moments from "@app/analytics/MeetingReport/components/Moments";
import { Moment } from "@app/analytics/MeetingReport/components/Moments/types";
import useSearchStore from "@app/analytics/MeetingReport/stores/search";
import { LoadingSkeleton } from "@app/analytics/MeetingReport/styles";
import { CoachingWord } from "@app/analytics/MeetingReport/types";
import { TOOLTIPS } from "@app/common/content";
import { pluralizePhrase } from "@app/common/utils";
import { getNonInclusiveTermsSemanticScore } from "@app/common/utils/coachingUtils";

import Stack from "@rui/Stack";
import { Bold } from "@rui/styles";

import useCoachingData from "./hooks/useCoachingData";
import InsufficientData from "./InsufficientData";
import { Description } from "./styles";

const NonInclusiveTermsSummary = ({
  instances,
  isEmpty,
  isLoading,
}: {
  instances: number;
  isEmpty: boolean;
  isLoading: boolean;
}) => {
  if (isLoading) {
    return <Skeleton width={120} />;
  }

  if (isEmpty) {
    return (
      <CoachingModuleEmptySummary>
        Not enough talk time
      </CoachingModuleEmptySummary>
    );
  }

  const semanticScore = getNonInclusiveTermsSemanticScore(instances);

  return (
    <BenchmarkOverlay
      renderControl={({ onClick }) => (
        <CoachingModuleSummary icon={semanticScore} onClick={onClick}>
          {pluralizePhrase(instances, "instance")}
        </CoachingModuleSummary>
      )}
      title="Benchmark"
    >
      <NonInclusiveTermsGradientRange count={instances} withMarkerLabel />
    </BenchmarkOverlay>
  );
};

const NonInclusiveTermsModule = ({
  onClickMoment,
  onClickWord,
}: {
  onClickMoment: (m: Moment) => (() => void) | undefined;
  onClickWord: (word: CoachingWord) => (() => void) | undefined;
}) => {
  const searchTerm = useSearchStore((state) => state.searchTerm);
  const {
    insufficientTalkTimeData,
    isError,
    isInitialLoading,
    nonInclusiveTerms,
    refetch,
  } = useCoachingData();

  const moments = nonInclusiveTerms.moments;

  // even if talk time was "too low", still show moments if any were detected
  const showEmptyState = insufficientTalkTimeData && isEmpty(moments);

  return (
    <CoachingModule expandable>
      <CoachingModuleHeader>
        <CoachingModuleTitle
          Icon={AnnotationX}
          tooltip={TOOLTIPS.coaching.nonInclusiveTerms}
        >
          Non-inclusive terms
        </CoachingModuleTitle>
        <NonInclusiveTermsSummary
          instances={nonInclusiveTerms.totalCount}
          isEmpty={showEmptyState}
          isLoading={isInitialLoading}
        />
      </CoachingModuleHeader>
      <CoachingModuleContent>
        {isError ? (
          <ErrorState refetch={refetch} sx={{ minHeight: "150px" }} />
        ) : showEmptyState ? (
          <InsufficientData type="talk_time">
            Your talk time was too low to detect non-inclusive terms.
          </InsufficientData>
        ) : (
          <Stack spacing={1}>
            {isInitialLoading ? (
              <LoadingSkeleton height={50} />
            ) : isEmpty(moments) ? (
              <Description>
                Read didn't detect that you used any non-inclusive terms in this
                meeting. Great job!
              </Description>
            ) : (
              <>
                <SelectableChipsContainer>
                  {nonInclusiveTerms.words.map((word) => {
                    const wordIsSelected = word.word === searchTerm;
                    return (
                      <SelectableChip
                        key={word.word}
                        label={`${word.displayValue} (${word.count})`}
                        onClick={onClickWord(word)}
                        variant={wordIsSelected ? "filled" : "outlined"}
                        color={wordIsSelected ? "primary" : "default"}
                      />
                    );
                  })}
                </SelectableChipsContainer>
                <Description>
                  Review{" "}
                  <Bold>{pluralizePhrase(moments.length, "moment")}</Bold> where
                  you used terms that are considered non-inclusive or offensive:
                </Description>
                <Moments moments={moments} onClickMoment={onClickMoment} />
              </>
            )}
          </Stack>
        )}
      </CoachingModuleContent>
    </CoachingModule>
  );
};

export default NonInclusiveTermsModule;
